import { Link, Text, HStack, VStack } from '@ayble-health/dashboard-components';
import { useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { useToken } from '@chakra-ui/react';
import { Medication, Prescription } from 'api/types';
import PharmacyItemRow from 'components/UserDashboard/overview/PharmacyItemRow';

type Props = {
  collapsible?: boolean;
  title: string;
  items?: (Medication | Prescription)[];
};

const PharmacySection = ({ title, items, collapsible }: Props) => {
  const [expanded, setExpanded] = useState(true);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [bluegrey] = useToken('colors', ['bluegrey']);

  const handleCollapse = () => {
    if (!collapsible) return;

    setExpanded(!expanded);
  };

  const handleOpenMenuChange = (id: string, isOpen: boolean) => {
    if (!isOpen) {
      setOpenMenuId((current) => (current === id ? null : current));
      return;
    }

    if (openMenuId && openMenuId !== id) {
      // Close the previously open menu first and let that commit settle
      // before opening a different one — flipping both in the same
      // update races Ark's dismissable/positioning teardown for the old
      // instance against the new instance's setup.
      setOpenMenuId(null);
      requestAnimationFrame(() => setOpenMenuId(id));
      return;
    }

    setOpenMenuId(id);
  };

  const header = (
    <HStack gap={2} alignItems={'center'}>
      <Text color={'bluegrey'} textTransform={'uppercase'} fontWeight={600}>
        {title}
      </Text>
      <Text color={'bluegrey'} fontWeight={400}>
        {items?.length ?? 0}
      </Text>
      {collapsible &&
        (expanded ? (
          <ChevronUpIcon size={16} color={bluegrey} />
        ) : (
          <ChevronDownIcon size={16} color={bluegrey} />
        ))}
    </HStack>
  );

  return (
    <VStack my={3} gap={2}>
      <HStack justifyContent={'space-between'}>
        {collapsible ? (
          <Link onClick={handleCollapse} textDecoration={'none'}>
            {header}
          </Link>
        ) : (
          header
        )}
      </HStack>

      {expanded &&
        items?.map((item) => {
          const { id } = item;
          return (
            <PharmacyItemRow
              key={id}
              item={item}
              isMenuOpen={openMenuId === id}
              onOpenMenuChange={(isOpen) => handleOpenMenuChange(id, isOpen)}
            />
          );
        })}
    </VStack>
  );
};

export default PharmacySection;
